interface ChartPage {
    nodes: Element[];
    nextUrl: string | null;
}

interface ChartInfiniteScrollOptions {
    root: Element;
    initialChartBlock: Element;
    initialUrl: string;
    fetchPage: (url: string) => Promise<ChartPage>;
    onNodesAdded: (nodes: Element[]) => void;
    onError?: (error: unknown) => void;
}

export function findAdNodes(nodes: Element[]): HTMLElement[] {
    return nodes.flatMap((node) => [
        ...(node.matches('[data-cnt][data-pbid]') ? [node as HTMLElement] : []),
        ...node.querySelectorAll<HTMLElement>('[data-cnt][data-pbid]'),
    ]);
}

function lastChartBlock(root: Element): Element | null {
    const chartBlocks = [...root.children].filter((node) => node.matches('section.items-grid'));

    return chartBlocks.at(-1) ?? null;
}

function removeFinishedSpinners(root: Element, hasNextPage: boolean): void {
    const spinners = [...root.querySelectorAll('.spinner')];

    if (hasNextPage) {
        spinners.pop();
    }

    spinners.forEach((spinner) => spinner.parentElement?.remove());
}

export function createChartInfiniteScroll({
    root,
    initialChartBlock,
    initialUrl,
    fetchPage,
    onNodesAdded,
    onError = (error) => console.error('Failed to fetch chart block:', error),
}: ChartInfiniteScrollOptions): IntersectionObserver {
    let nextUrl: string | null = initialUrl;
    let observedChartBlock = initialChartBlock;
    let isLoading = false;

    const observer = new IntersectionObserver(
        (entries) => {
            const shouldLoad = entries.some(
                (entry) => entry.target === observedChartBlock && entry.isIntersecting,
            );

            if (!shouldLoad || isLoading || nextUrl === null) {
                return;
            }

            isLoading = true;
            observer.unobserve(observedChartBlock);

            void fetchPage(nextUrl)
                .then((page) => {
                    root.append(...page.nodes);
                    onNodesAdded(page.nodes);
                    nextUrl = page.nextUrl;
                    removeFinishedSpinners(root, nextUrl !== null);

                    const nextChartBlock = lastChartBlock(root);
                    if (nextUrl !== null && nextChartBlock !== null) {
                        observedChartBlock = nextChartBlock;
                        observer.observe(observedChartBlock);
                    } else if (nextUrl !== null) {
                        nextUrl = null;
                        removeFinishedSpinners(root, false);
                        onError(new Error('Chart page did not contain a chart block.'));
                    }
                })
                .catch((error: unknown) => {
                    onError(error);
                    isLoading = false;
                    observer.observe(observedChartBlock);
                })
                .finally(() => {
                    isLoading = false;
                });
        },
        {
            root: null,
            rootMargin: '400px',
        },
    );

    observer.observe(initialChartBlock);

    return observer;
}
